import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { UserListSearchField } from '@src/models/mappers/UserMapper';
import { useDebouncedValue } from '@src/packages/utils/hooks';
import UserService from '@src/services/UserService';

import {
  createPageTokenMap,
  fetchWithJourneyPage,
  type PageTokenMap,
} from '../utils/withJourneyPagination';

const SEARCH_DEBOUNCE_MS = 500;

export const USERS_SELECTION_DRAWER_SEARCH_FIELDS: UserListSearchField[] = [
  'firstName',
  'lastName',
  'email',
  'idCard',
];

type UseWithJourneyUsersPaginationOptions = {
  isVisible: boolean;
  organizationId: string | undefined;
  searchTerm?: string;
  searchFields?: UserListSearchField[];
  /** When set, restricts results to users on this work shift (backend filter). */
  workShiftId?: string;
  /** When set, restricts results to users assigned to this work journey config (backend filter). */
  workJourneyConfigId?: string;
  defaultPageSize?: number;
};

type UseWithJourneyUsersPaginationResult = {
  allUsers: unknown[];
  usersLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  handlePageChange: (page: number, size: number) => void;
  resetPagination: () => void;
};

/**
 * Paginates assignable users via getWithJourney (org users + info, optional searchTerm filter).
 */
export function useWithJourneyUsersPagination(
  options: UseWithJourneyUsersPaginationOptions,
): UseWithJourneyUsersPaginationResult {
  const {
    isVisible,
    organizationId: orgId,
    searchTerm = '',
    searchFields = USERS_SELECTION_DRAWER_SEARCH_FIELDS,
    workShiftId,
    workJourneyConfigId,
    defaultPageSize = 10,
  } = options;

  const debouncedSearch = useDebouncedValue(searchTerm.trim(), SEARCH_DEBOUNCE_MS);

  const userServiceRef = useRef<UserService | null>(null);
  if (userServiceRef.current === null) {
    userServiceRef.current = new UserService();
  }
  const userService = userServiceRef.current;

  const [allUsers, setAllUsers] = useState<unknown[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const inputTokenByPageRef = useRef<PageTokenMap>(createPageTokenMap());
  const [paginationResetKey, setPaginationResetKey] = useState(0);

  const resetPagination = useCallback(() => {
    inputTokenByPageRef.current = createPageTokenMap();
    setCurrentPage(1);
    setPageSize(defaultPageSize);
    setTotalCount(0);
    setAllUsers([]);
  }, [defaultPageSize]);

  const fetchPage = useCallback(
    async ({ pageSize: s, pageToken }: { pageSize: number; pageToken: string | null }) => {
      return userService.getUsersWithJourney({
        organizationId: orgId as string,
        pageSize: s,
        pageToken,
        ...(debouncedSearch ? { searchTerm: debouncedSearch, searchFields } : {}),
        ...(workShiftId ? { workShiftId } : {}),
        ...(workJourneyConfigId ? { workJourneyConfigId } : {}),
      });
    },
    [orgId, userService, debouncedSearch, searchFields, workShiftId, workJourneyConfigId],
  );

  useLayoutEffect(() => {
    if (!isVisible || !orgId) {
      return;
    }
    inputTokenByPageRef.current = createPageTokenMap();
    setCurrentPage(1);
    setPaginationResetKey(k => k + 1);
  }, [isVisible, orgId, workShiftId, workJourneyConfigId, debouncedSearch]);

  useEffect(() => {
    if (!isVisible || !orgId) {
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    const run = async () => {
      setUsersLoading(true);
      try {
        const outcome = await fetchWithJourneyPage({
          map: inputTokenByPageRef.current,
          page: currentPage,
          pageSize,
          fetchPage,
          isCancelled,
        });

        if (outcome.status === 'cancelled') {
          return;
        }
        if (outcome.status === 'empty') {
          setAllUsers([]);
          setTotalCount(outcome.total);
          return;
        }
        setAllUsers(outcome.users);
        setTotalCount(outcome.total);
      } catch (err) {
        console.error('Error fetching users with journey:', err);
        if (!cancelled) {
          setAllUsers([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isVisible, orgId, currentPage, pageSize, paginationResetKey, fetchPage]);

  const handlePageChange = useCallback(
    (page: number, size: number) => {
      if (size !== pageSize) {
        inputTokenByPageRef.current = createPageTokenMap();
        setPageSize(size);
        setCurrentPage(1);
        setPaginationResetKey(k => k + 1);
        return;
      }
      setCurrentPage(page);
    },
    [pageSize],
  );

  return {
    allUsers,
    usersLoading,
    currentPage,
    pageSize,
    totalCount,
    handlePageChange,
    resetPagination,
  };
}
